import { supabase } from '@/lib/supabase'

const BASE = '/api/v1/crm'

/** A failed CRM call with the backend's Dutch message and machine-readable code. */
export class CrmApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly body: Record<string, unknown> | null

  constructor(
    message: string,
    status: number,
    code: string | null,
    body: Record<string, unknown> | null,
  ) {
    super(message)
    this.name = 'CrmApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

/**
 * The message to show for an error body: `{"error": …}` from the spec-63
 * endpoints, or the first validator message of a FastAPI 422.
 */
export function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>
    if (typeof record.error === 'string' && record.error) return record.error
    if (Array.isArray(record.detail) && record.detail.length > 0) {
      const first = record.detail[0] as { msg?: unknown }
      if (typeof first.msg === 'string') return first.msg.replace(/^Value error, /, '')
    }
  }
  return `Verzoek mislukt (${status})`
}

/**
 * A request that got no usable answer. The browser's own error for it is English
 * ("Failed to fetch", "Load failed"), and screens show `error.message` as it is, so it
 * becomes a Dutch CrmApiError. An abort the caller asked for stays what it is, so the
 * caller can still recognise it.
 */
function transportError(caught: unknown, init: RequestInit | undefined): unknown {
  if (init?.signal?.aborted) return caught
  return new CrmApiError('Geen verbinding met de server.', 0, 'network', null)
}

/** The headers of every CRM call: JSON, and the Supabase bearer token when there is a session. */
async function crmHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`
  return headers
}

/** The answer of a CRM call, or the CrmApiError of its failure (a bad status or no connection). */
async function crmFetch(path: string, init: RequestInit | undefined): Promise<Response> {
  const headers = await crmHeaders()
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch (caught) {
    throw transportError(caught, init)
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : null
    const code = typeof record?.code === 'string' ? record.code : null
    throw new CrmApiError(errorMessage(body, response.status), response.status, code, record)
  }
  return response
}

/** JSON request to `/api/v1/crm` with the Supabase bearer token. */
export async function crmRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await crmFetch(path, init)
  if (response.status === 204) return undefined as T
  try {
    return (await response.json()) as T
  } catch (caught) {
    // No JSON (a proxy's error page, say), or the answer broke off halfway.
    if (caught instanceof SyntaxError) {
      const message = 'Onverwacht antwoord van de server.'
      throw new CrmApiError(message, response.status, 'invalid_response', null)
    }
    throw transportError(caught, init)
  }
}

/**
 * POSTs a JSON body and saves the answer as a file (the xlsx exports). A refusal or a lost
 * connection is a CrmApiError, like `crmRequest`; nothing is saved then.
 */
export async function crmDownload(path: string, body: unknown, filename: string): Promise<void> {
  const init: RequestInit = { method: 'POST', body: JSON.stringify(body) }
  const response = await crmFetch(path, init)
  let blob: Blob
  try {
    blob = await response.blob()
  } catch (caught) {
    throw transportError(caught, init) // the file broke off halfway
  }
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}
